import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import exchangeService from '../../services/exchangeService';
import api from '../../utils/api';
import ReportModal from './ReportModal';
import { FaSpinner, FaPaperPlane, FaFlag, FaVideo, FaArrowLeft, FaPhoneSlash } from 'react-icons/fa';
import '../../ChatRoom.css';

// 매칭 1개(matchId)에 대한 실제 1:1 채팅방이다.
// 실시간이라고는 해도 웹소켓을 새로 깔지 않고, 3초마다 "전체 메시지 다시 줘"라고 물어보는
// 방식(폴링)으로 만들었다. afterId로 새 메시지만 받는 대신 매번 전체를 다시 받는 이유는,
// 그래야 "내가 보낸 메시지를 상대가 읽었는지(isRead)"도 매번 최신 상태로 같이 갱신되기 때문이다
// (카카오톡의 "1"이 사라지는 것과 같은 효과). 채팅방 하나의 메시지 수가 아주 많지 않은
// 초기 단계 규모에서는 이 방식이 제일 간단하고 안전하다.
const ChatRoom = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();

  const [myUserId, setMyUserId] = useState(null);
  const [partner, setPartner] = useState(null); // { partnerUserId, partnerNickname }
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);
  // 상대가 먼저 통화를 시작했는지 여부. true가 되면 헤더 아래에 "참여하기/끊기" 배너를 띄운다.
  const [partnerCallActive, setPartnerCallActive] = useState(false);
  const [partnerCallSessionId, setPartnerCallSessionId] = useState(null);
  const [decliningCall, setDecliningCall] = useState(false);
  // 통화가 방금 끝났음(active: true -> false로 바뀌는 순간)을 잠시 안내하기 위한 상태다.
  // 배너가 그냥 사라지면 사용자가 "끝난건가 안 끝난건가" 헷갈려할 수 있어서,
  // 명시적으로 "통화가 종료되었다"를 보여주고 참여하기 버튼은 비활성화한 다음 몇 초 뒤에 지운다.
  const [callJustEnded, setCallJustEnded] = useState(false);
  const prevCallActiveRef = useRef(false);
  const callEndedTimeoutRef = useRef(null);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    initChatRoom();
    // 3초마다 전체 메시지 + 읽음 상태를 다시 확인한다.
    const messageIntervalId = setInterval(pollMessages, 3000);
    // 3초마다 상대가 통화를 시작했는지도 같이 확인한다(메시지 폴링과 별개 간격으로 관리).
    const callIntervalId = setInterval(pollCallStatus, 3000);
    return () => {
      clearInterval(messageIntervalId);
      clearInterval(callIntervalId);
      if (callEndedTimeoutRef.current) clearTimeout(callEndedTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const initChatRoom = async () => {
    try {
      setLoading(true);
      setErrorMessage('');

      const [meRes, matchesRes, messagesRes] = await Promise.all([
        api.get('/api/users/me'),
        exchangeService.getMyMatches(),
        exchangeService.getMessages(matchId),
      ]);

      setMyUserId(meRes.data.id);

      const matchInfo = (matchesRes.data || []).find((m) => String(m.id) === String(matchId));
      if (!matchInfo) {
        setErrorMessage('매칭 정보를 찾을 수 없다. 채팅함으로 돌아가달라.');
        return;
      }
      setPartner(matchInfo);
      setMessages(messagesRes.data || []);

      // 채팅방에 들어온 순간, 상대가 보낸 메시지를 전부 읽음 처리한다(카카오톡의 "1" 없어지는 것과 동일).
      markAsReadSafely();
      // 들어오자마자 지금 상대가 통화 중인지도 바로 확인한다(3초 폴링 첫 회차를 기다리지 않고).
      pollCallStatus();
    } catch (err) {
      console.error('채팅방 초기화 오류:', err);
      setErrorMessage('채팅방을 불러오는 중 오류가 발생했다.');
    } finally {
      setLoading(false);
    }
  };

  // 폴링: 전체 메시지를 다시 받아서 새 메시지 + 읽음 상태 변화를 모두 반영한다.
  const pollMessages = useCallback(async () => {
    try {
      const response = await exchangeService.getMessages(matchId);
      setMessages(response.data || []);
      markAsReadSafely();
    } catch (err) {
      // 폴링 중 오류는 화면에 계속 에러를 띄우면 거슬리니까 콘솔에만 남긴다.
      console.error('메시지 폴링 오류:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  // 지금 이 매칭방에 종료 안 된 통화 세션이 있는지 확인한다.
  // 상대가 먼저 "영상통화" 버튼을 누르면, 내가 별도로 누르지 않아도 이 폴링이 그것을 잡아서
  // 확인되면 배너를 띄우고, 종료되면(active: false) 다시 사라진다.
  const pollCallStatus = useCallback(async () => {
    try {
      const response = await exchangeService.getCallStatus(matchId);
      const active = Boolean(response.data?.active);

      // 방금까지 활성(true)이었다가 이번 폴링에서 비활성(false)으로 바뀌었다면,
      // 상대(또는 나)가 방금 통화를 끝낸 거다. "통화가 종료되었다"를 명시적으로
      // 잠시 보여주고, 참여하기 버튼은 그 동안 비활성화해둔다.
      if (prevCallActiveRef.current && !active) {
        setCallJustEnded(true);
        if (callEndedTimeoutRef.current) clearTimeout(callEndedTimeoutRef.current);
        callEndedTimeoutRef.current = setTimeout(() => setCallJustEnded(false), 5000);
      } else if (active) {
        // 새로 통화가 시작되면(또는 여전히 진행 중이면) 종료 안내는 지운다.
        setCallJustEnded(false);
        if (callEndedTimeoutRef.current) {
          clearTimeout(callEndedTimeoutRef.current);
          callEndedTimeoutRef.current = null;
        }
      }
      prevCallActiveRef.current = active;

      setPartnerCallActive(active);
      setPartnerCallSessionId(active ? response.data?.callSessionId : null);
    } catch (err) {
      // 통화 상태 확인 실패는 채팅 자체를 막을 이유가 없으므로 콘솔에만 남긴다.
      console.error('통화 상태 폴링 오류:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  // 배너에서 "끊기"를 누르면, 내가 아직 참여하지 않은 통화를 거절하는 것이다.
  // 서버에 endCall을 바로 호출해서 세션을 종료시키면, 상대(통화를 건 사람)의 CallRoom.js도
  // 폴링으로 이를 감지해서 자동으로 방을 닫는다.
  const handleDeclineCall = async () => {
    if (!partnerCallSessionId || decliningCall) return;
    try {
      setDecliningCall(true);
      await exchangeService.endCall(partnerCallSessionId);
      setPartnerCallActive(false);
      setPartnerCallSessionId(null);
    } catch (err) {
      console.error('통화 거절 오류:', err);
      alert('통화 거절 중 오류가 발생했다.');
    } finally {
      setDecliningCall(false);
    }
  };

  const markAsReadSafely = async () => {
    try {
      await exchangeService.markMessagesRead(matchId);
    } catch (err) {
      console.error('읽음 처리 오류:', err);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) {
      return;
    }
    try {
      setSending(true);
      const response = await exchangeService.sendMessage(matchId, newMessage.trim());
      const sentMessage = response.data;
      setMessages((prev) => [...prev, sentMessage]);
      setNewMessage('');
    } catch (err) {
      console.error('메시지 전송 오류:', err);
      alert('메시지 전송 중 오류가 발생했다.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="exchange-loading">
        <FaSpinner className="exchange-spinner" />
        <p>불러오는 중이다...</p>
      </div>
    );
  }

  if (errorMessage && !partner) {
    return (
      <div className="chat-room-error-container">
        <p>{errorMessage}</p>
        <Link to="/exchange/chats">채팅함으로 돌아가기</Link>
      </div>
    );
  }

  return (
    <div className="chat-room-container">
      <div className="chat-room-header">
        <button className="chat-room-back-button" onClick={() => navigate('/exchange/chats')}>
          <FaArrowLeft />
        </button>
        <span className="chat-room-header-nickname">{partner?.partnerNickname}</span>
        <div className="chat-room-header-actions">
          <Link to={`/exchange/call/${matchId}`} className="chat-room-call-button">
            <FaVideo /> 영상통화
          </Link>
          {/* 지금 이 매칭에 진행 중인 통화 세션이 있으면(내가 시작했든 상대가 시작했든 상관없이),
              채팅방을 떠나지 않고도 바로 끝낼 수 있게 "영상통화" 글자 바로 옷에 "통화 종료" 버튼을 띄운다.
              누르면 handleDeclineCall이 서버에 endCall을 호출하고, 백엔드(CallServiceImpl.endCall)가
              DB 기록과 동시에 Daily.co 방도 실제로 삭제한다. */}
          {partnerCallActive && (
            <button
              type="button"
              className="chat-room-end-call-header-button"
              onClick={handleDeclineCall}
              disabled={decliningCall}
            >
              <FaPhoneSlash /> 통화 종료
            </button>
          )}
          <button className="chat-room-report-button" onClick={() => setShowReportModal(true)}>
            <FaFlag /> 신고
          </button>
        </div>
      </div>

      {partnerCallActive && (
        <div className="chat-room-call-banner">
          <FaVideo />
          <span>{partner?.partnerNickname}님이 영상통화를 시작했다</span>
          <div className="chat-room-call-banner-actions">
            <Link to={`/exchange/call/${matchId}`} className="chat-room-call-banner-join">
              참여하기
            </Link>
            <button
              type="button"
              className="chat-room-call-banner-decline"
              onClick={handleDeclineCall}
              disabled={decliningCall}
            >
              끊기
            </button>
          </div>
        </div>
      )}

      {!partnerCallActive && callJustEnded && (
        <div className="chat-room-call-banner chat-room-call-banner-ended">
          <FaVideo />
          <span>통화가 종료되었다</span>
          <div className="chat-room-call-banner-actions">
            <span className="chat-room-call-banner-join chat-room-call-banner-join-disabled">
              참여하기
            </span>
          </div>
        </div>
      )}

      <div className="chat-room-messages">
        {messages.map((message) => {
          const isMine = message.senderId === myUserId;
          return (
            <div key={message.id} className={`chat-message-row ${isMine ? 'mine' : 'partner'}`}>
              {!isMine && <span className="chat-message-sender">{message.senderNickname}</span>}
              <div className="chat-message-line">
                {/* 카카오톡처럼, 내가 보낸 메시지에 상대가 아직 안 읽었으면 "1"을 보여주고
                    상대가 읽으면(isRead === true) 다음 폴링에서 사라진다. */}
                {isMine && (
                  <div className="chat-message-meta">
                    {message.isRead === false && <span className="chat-message-unread-mark">1</span>}
                    <span className="chat-message-time">
                      {new Date(message.createdAt).toLocaleTimeString('ko-KR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                )}
                <div className="chat-message-bubble">{message.content}</div>
                {!isMine && (
                  <span className="chat-message-time">
                    {new Date(message.createdAt).toLocaleTimeString('ko-KR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <form className="chat-room-input-area" onSubmit={handleSend}>
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="메시지를 입력해달라"
          maxLength={1000}
        />
        <button type="submit" disabled={sending || !newMessage.trim()}>
          <FaPaperPlane />
        </button>
      </form>

      {showReportModal && (
        <ReportModal
          targetId={partner?.partnerUserId}
          targetNickname={partner?.partnerNickname}
          onClose={() => setShowReportModal(false)}
        />
      )}
    </div>
  );
};

export default ChatRoom;
